import React, { useState } from "react"
import Money from "components/Money"
import DineroFactory from "dinero.js"
import dayjs from "dayjs"
import { getPaymentProviderDisplayName } from "helpers/getPaymentDisplayName"
import FlagIcon from "../Ui/FlagIcon"
import { ShopperInsightsSearchHitCheckout } from "lib/types/generated/graphql-types"
import { Tooltip } from "react-tooltip"
import { ReactComponent as DownIcon } from "images/icons/chevron-down.svg"
import OrderLineRow from "../Customer/OrderLineRow"
import {
  CheckoutCell,
  CheckoutDetailsContainer,
  CheckoutId,
  CopyButton,
  CustomerEmail,
  DateTime,
  ExpandableContent,
  ExpandButton,
  ItemCount,
  OrderLinesHeader,
  OrderLinesTableHeader,
  OrderReference,
  PaymentStatusBadge,
  Section,
  TableRow,
  TotalAmount
} from "./InsightsCheckoutTable.styled"
import { ReactComponent as CopyIcon } from "images/icons/copy.svg"
import { ReactComponent as RequestIcon } from "images/icons/stopwatch.svg"
import { ReactComponent as FailedIcon } from "images/icons/xmark-thick.svg"
import { ReactComponent as PlayIcon } from "images/icons/play.svg"
import { ReactComponent as CompletedIcon } from "images/icons/flag-checkered.svg"
import { ReactComponent as CreatedIcon } from "images/icons/square-plus.svg"

import { CustomerOrders } from "../../classes/customer/CustomerOrders"

type Props = {
  checkout: ShopperInsightsSearchHitCheckout
  customerOrders: CustomerOrders
  sidebarCollapsed: boolean
}

const InsightsCheckoutRow = ({ checkout, customerOrders, sidebarCollapsed }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const checkoutTotal =
    checkout.items?.reduce((sum, item) => sum + (item.totalPriceAmount || 0), 0) || 0
  const itemCount = checkout.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0
  const paymentStatus = checkout.paymentStatus?.current || "UNKNOWN"
  const tooltipId = `payment-status-${checkout.id}`

  const getPaymentStatusMessage = () => {
    const latestLog = checkout.paymentStatus?.log?.[0]
    if (latestLog?.message) {
      return `${paymentStatus}: ${latestLog.message}`
    }

    switch (paymentStatus) {
      case "ON_HOLD":
        return "Payment on hold at PSP"
      case "REJECTED":
        return "Payment rejected at PSP"
      case "STARTED":
        return "Payment started at PSP, but not finished yet"
      default:
        return `Payment status: ${paymentStatus}`
    }
  }

  const renderStatusIcon = () => {
    switch (paymentStatus) {
      case "REJECTED":
        return <FailedIcon />
      case "PENDING":
        return <CreatedIcon />
      case "ON_HOLD":
        return <RequestIcon />
      case "STARTED":
        return <PlayIcon />
      case "COMPLETED":
        return <CompletedIcon />
      default:
        return null
    }
  }

  const handleCopyToClipboard = (text: string, event: React.MouseEvent) => {
    event.stopPropagation()
    navigator.clipboard.writeText(text)
  }

  return (
    <>
      <TableRow style={{ cursor: "pointer" }} onClick={() => setIsExpanded(!isExpanded)}>
        <CheckoutCell>
          <CheckoutDetailsContainer>
            <ExpandButton $expanded={isExpanded} className="expand-button">
              <DownIcon />
            </ExpandButton>
            <div>
              <CheckoutId>Checkout #{checkout.id?.slice(-8)}</CheckoutId>
              <CustomerEmail>{checkout.shippingAddress?.email || "No email"}</CustomerEmail>
              {checkout.orderReference && (
                <OrderReference>
                  Order: {checkout.orderReference}
                  <CopyButton onClick={(e) => handleCopyToClipboard(checkout.orderReference, e)}>
                    <CopyIcon />
                  </CopyButton>
                </OrderReference>
              )}
              <ItemCount>
                {itemCount} {itemCount === 1 ? "item" : "items"}
              </ItemCount>
            </div>
          </CheckoutDetailsContainer>
        </CheckoutCell>

        <Section className="hideOnMediumScreens">
          {customerOrders?.getStoreGroupName(checkout.storeGroupId) || "-"}
        </Section>

        <Section className="hideOnSmallScreens">
          {checkout.countryCode ? <FlagIcon countryCode={checkout.countryCode} /> : "-"}
        </Section>

        {sidebarCollapsed ? (
          <Section className="hideOnMediumScreens">
            {checkout.providers?.payment?.providerName
              ? getPaymentProviderDisplayName(checkout.providers.payment.providerName)
              : "-"}
          </Section>
        ) : (
          <></>
        )}

        <Section className="hideOnSmallScreens">
          <TotalAmount>
            <Money
              amount={checkoutTotal}
              currencyUnit={(checkout.currencyCode as DineroFactory.Currency) || "USD"}
            />
          </TotalAmount>
        </Section>

        <Section>
          <Tooltip id={tooltipId}>
            <div>{getPaymentStatusMessage()}</div>
          </Tooltip>
          <PaymentStatusBadge $status={paymentStatus} data-tooltip-id={tooltipId}>
            {renderStatusIcon()}
          </PaymentStatusBadge>
        </Section>

        <Section className="hideOnMediumScreens">
          {checkout.paymentStatus?.log?.[checkout.paymentStatus?.log?.length - 1]?.message || "-"}
        </Section>
        <Section className="hideOnMediumScreens">
          <DateTime>{dayjs(checkout.created).format("YYYY-MM-DD HH:mm")}</DateTime>
        </Section>
      </TableRow>

      {isExpanded && checkout.items && checkout.items.length > 0 && (
        <ExpandableContent>
          <OrderLinesHeader>
            Order Items ({checkout.items.length} {checkout.items.length === 1 ? "item" : "items"})
          </OrderLinesHeader>
          <OrderLinesTableHeader>
            <div style={{ paddingLeft: "1rem" }}>Product</div>
            <div style={{ textAlign: "center" }}>Qty</div>
            <div style={{ textAlign: "right" }}>Unit Price</div>
            <div style={{ textAlign: "right" }}>Total</div>
            <div></div>
          </OrderLinesTableHeader>
          {checkout.items.map((item) => (
            <OrderLineRow key={item.id} item={item} currencyCode={checkout.currencyCode} />
          ))}
        </ExpandableContent>
      )}
    </>
  )
}

export default InsightsCheckoutRow
